import React, { useState, useEffect, useRef } from "react";
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  FlatList,
  TextInput,
  ActivityIndicator,
  Alert,
  SafeAreaView,
  Platform,
  Modal,
  RefreshControl,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import {
  getAllOpportunitiesForAdmin,
  getFeaturedOpportunityId,
  setFeaturedOpportunityId,
  requestOpportunityDeletion,
} from "../../services/parseService";
import { Opportunity, OpportunityStatus } from "../../types";
import { format, parseISO } from "date-fns";

interface OpportunityListScreenProps {
  onEditOpportunity: (opportunityId: string) => void;
  onCreateOpportunity: () => void;
}

const OpportunityListScreen: React.FC<OpportunityListScreenProps> = ({
  onEditOpportunity,
  onCreateOpportunity,
}) => {
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [filteredOpportunities, setFilteredOpportunities] = useState<
    Opportunity[]
  >([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<OpportunityStatus | "All">(
    "All",
  );
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const [featuredOpportunityId, setFeaturedOpportunityIdState] = useState<string | null>(null);
  const [isSettingFeatured, setIsSettingFeatured] = useState(false);

  const [opportunityToDelete, setOpportunityToDelete] = useState<Opportunity | null>(null);
  const [deleteReason, setDeleteReason] = useState("");
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [isSubmittingDeleteRequest, setIsSubmittingDeleteRequest] = useState(false);

  const mountedRef = useRef(true);

  useEffect(() => {
    loadOpportunities();
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    filterOpportunities();
  }, [searchQuery, statusFilter, opportunities]);

  const loadOpportunities = async () => {
    if (!mountedRef.current) return;
    try {
      setIsLoading(true);
      setError(null);

      const [results, featuredId] = await Promise.all([
        getAllOpportunitiesForAdmin(500), // Retrieve up to 500 opportunities (including hidden ones)
        getFeaturedOpportunityId(),
      ]);

      if (!mountedRef.current) return;
      setFeaturedOpportunityIdState(featuredId);

      // Convert Parse objects to regular objects with proper date handling
      const parsedOpportunities = results.map((opp: any) => {
        if (opp.attributes) {
          const id = opp.id;
          const attributes = { ...opp.attributes };

          // Process dates
          const parseDate = (dateObject: any) => {
            if (!dateObject) return null;
            if (dateObject.iso) return dateObject.iso;
            if (dateObject instanceof Date) return dateObject.toISOString();
            return dateObject;
          };

          return {
            id,
            ...attributes,
            startDate: attributes.startDate
              ? parseDate(attributes.startDate)
              : null,
            endDate: attributes.endDate ? parseDate(attributes.endDate) : null,
            deadline: attributes.deadline
              ? parseDate(attributes.deadline)
              : null,
            applicationOpenDate: attributes.applicationOpenDate
              ? parseDate(attributes.applicationOpenDate)
              : null,
            applicationCloseDate: attributes.applicationCloseDate
              ? parseDate(attributes.applicationCloseDate)
              : null,
            createdAt: attributes.createdAt
              ? parseDate(attributes.createdAt)
              : null,
            updatedAt: attributes.updatedAt
              ? parseDate(attributes.updatedAt)
              : null,
          };
        }
        return opp;
      });

      setOpportunities(parsedOpportunities);
      setFilteredOpportunities(parsedOpportunities);
      setLastUpdated(new Date());
    } catch (err) {
      console.error("Error loading opportunities:", err);
      if (mountedRef.current) {
        setError("Failed to load opportunities. Please try again.");
      }
    } finally {
      if (mountedRef.current) {
        setIsLoading(false);
        setIsRefreshing(false);
      }
    }
  };

  const handleRefresh = () => {
    setIsRefreshing(true);
    loadOpportunities();
  };

  const filterOpportunities = () => {
    let filtered = [...opportunities];

    // Apply status filter
    if (statusFilter !== "All") {
      filtered = filtered.filter((opp) => opp.status === statusFilter);
    }

    // Apply search query
    if (searchQuery.trim() !== "") {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter((opp) => {
        const title = opp.title.toLowerCase().includes(query);
        const organization = opp.organization.toLowerCase().includes(query);

        // Handle both string and array type
        let typeMatch = false;
        if (Array.isArray(opp.type)) {
          typeMatch = opp.type.some((t) => t.toLowerCase().includes(query));
        } else if (typeof opp.type === "string") {
          typeMatch = opp.type.toLowerCase().includes(query);
        }

        return title || organization || typeMatch;
      });
    }

    setFilteredOpportunities(filtered);
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return "N/A";
    try {
      return format(parseISO(dateString), "MMM d, yyyy");
    } catch (err) {
      console.error("Error formatting date:", err, dateString);
      return "Invalid date";
    }
  };

  const getStatusColor = (status: OpportunityStatus) => {
    switch (status) {
      case "Open":
        return "#16A34A";
      case "Coming Soon":
        return "#D97706";
      case "Ongoing":
        return "#2563EB";
      case "Closed":
        return "#DC2626";
      default:
        return "#6B7280";
    }
  };

  const handleDeleteOpportunity = (opportunity: Opportunity) => {
    setOpportunityToDelete(opportunity);
    setDeleteReason("");
    setIsDeleteModalVisible(true);
  };

  const submitDeletionRequest = async () => {
    if (!opportunityToDelete) {
      console.warn("submitDeletionRequest called without opportunityToDelete");
      return;
    }

    // Prevent duplicate submissions
    if (isSubmittingDeleteRequest) {
      console.warn("Deletion request already in progress");
      return;
    }

    try {
      setIsSubmittingDeleteRequest(true);
      await requestOpportunityDeletion({
        opportunityId: opportunityToDelete.id,
        reason: deleteReason.trim() || undefined,
      });
      
      if (!mountedRef.current) return;
      
      if (Platform.OS === "web") {
        alert("Deletion request submitted for super admin review.");
      } else {
        Alert.alert("Requested", "Deletion request sent for approval.");
      }
      setIsDeleteModalVisible(false);
      setOpportunityToDelete(null);
      setDeleteReason("");
    } catch (err) {
      console.error("Error submitting deletion request:", err);
      if (!mountedRef.current) return;
      
      const errorMessage = err instanceof Error ? err.message : String(err);
      if (Platform.OS === "web") {
        alert(`Failed: ${errorMessage}`);
      } else {
        Alert.alert("Error", errorMessage);
      }
    } finally {
      if (mountedRef.current) {
        setIsSubmittingDeleteRequest(false);
      }
    }
  };

  const handleSetFeatured = async (opportunityId: string) => {
    try {
      setIsSettingFeatured(true);
      const success = await setFeaturedOpportunityId(opportunityId);
      
      if (success) {
        setFeaturedOpportunityIdState(opportunityId);
        if (Platform.OS === "web") {
          alert("Featured opportunity updated successfully!");
        } else {
          Alert.alert("Success", "Featured opportunity updated successfully!");
        }
      } else {
        throw new Error("Failed to update featured opportunity");
      }
    } catch (error) {
      console.error("Error setting featured opportunity:", error);
      const errorMessage = error instanceof Error ? error.message : "Failed to update featured opportunity";
      
      if (Platform.OS === "web") {
        alert(errorMessage);
      } else {
        Alert.alert("Error", errorMessage);
      }
    } finally {
      setIsSettingFeatured(false);
    }
  };

  const handleRemoveFeatured = async () => {
    try {
      setIsSettingFeatured(true);
      const success = await setFeaturedOpportunityId(null);
      
      if (success) {
        setFeaturedOpportunityIdState(null);
        if (Platform.OS === "web") {
          alert("Featured opportunity removed successfully!");
        } else {
          Alert.alert("Success", "Featured opportunity removed successfully!");
        }
      } else {
        throw new Error("Failed to remove featured opportunity");
      }
    } catch (error) {
      console.error("Error removing featured opportunity:", error);
      const errorMessage = error instanceof Error ? error.message : "Failed to remove featured opportunity";
      
      if (Platform.OS === "web") {
        alert(errorMessage);
      } else {
        Alert.alert("Error", errorMessage);
      }
    } finally {
      setIsSettingFeatured(false);
    }
  };

  const renderStatusFilter = () => {
    const statuses: (OpportunityStatus | "All")[] = [
      "All",
      "Open",
      "Ongoing", 
      "Coming Soon",
      "Closed",
    ];

    return (
      <View style={styles.statusFilterContainer}>
        {statuses.map((status) => (
          <TouchableOpacity
            key={status}
            style={[
              styles.statusFilterButton,
              statusFilter === status && styles.statusFilterButtonActive,
            ]}
            onPress={() => setStatusFilter(status)}
            activeOpacity={0.7}
          >
            <Text
              style={[
                styles.statusFilterText,
                statusFilter === status && styles.statusFilterTextActive,
              ]}
            >
              {status}
            </Text>
          </TouchableOpacity>
        ))}
      </View>
    );
  };

  const renderItem = ({ item }: { item: Opportunity }) => (
    <View style={styles.opportunityCard}>
      {/* Card Header */}
      <View style={styles.cardHeader}>
        <View style={styles.cardTitleSection}>
          <Text style={styles.opportunityTitle} numberOfLines={2}>
            {item.title}
          </Text>
          <Text style={styles.organizationText}>{item.organization}</Text>
        </View>
        <View style={styles.cardHeaderActions}>
          <View
            style={[
              styles.statusBadge,
              { backgroundColor: getStatusColor(item.status) + '15' },
              { borderColor: getStatusColor(item.status) + '40' },
            ]}
          >
            <Text style={[styles.statusText, { color: getStatusColor(item.status) }]}>
              {item.status}
            </Text>
          </View>
        </View>
      </View>

      {/* Card Content */}
      <View style={styles.cardContent}>
        {/* Type and Location Row */}
        <View style={styles.detailsRow}>
          {item.type && (
            <View style={styles.detailBadge}>
              <Ionicons name="pricetag-outline" size={14} color="#6B7280" />
              <Text style={styles.detailText}>
                {Array.isArray(item.type) ? item.type[0] : item.type}
                {Array.isArray(item.type) && item.type.length > 1 && ` +${item.type.length - 1}`}
              </Text>
            </View>
          )}
          <View style={styles.detailBadge}>
            <Ionicons name="location-outline" size={14} color="#6B7280" />
            <Text style={styles.detailText}>{item.location}</Text>
          </View>
        </View>

        {/* Dates Row */}
        <View style={styles.detailsRow}>
          <View style={styles.detailBadge}>
            <Ionicons name="calendar-outline" size={14} color="#6B7280" />
            <Text style={styles.detailText}>
              {formatDate(item.startDate)} - {formatDate(item.endDate)}
            </Text>
          </View>
        </View>

        {/* Application Dates */}
        {item.applicationOpenDate && item.applicationCloseDate && (
          <View style={styles.detailsRow}>
            <View style={styles.detailBadge}>
              <Ionicons name="time-outline" size={14} color="#6B7280" />
              <Text style={styles.detailText}>
                Apply: {formatDate(item.applicationOpenDate)} - {formatDate(item.applicationCloseDate)}
              </Text>
            </View>
          </View>
        )}

        {/* Description Preview */}
        {item.description && (
          <Text style={styles.descriptionPreview} numberOfLines={2}>
            {item.description}
          </Text>
        )}

        {/* Tags */}
        {item.tags && item.tags.length > 0 && (
          <View style={styles.tagsRow}>
            {item.tags.slice(0, 3).map((tag, index) => (
              <View key={index} style={styles.tag}>
                <Text style={styles.tagText}>{tag}</Text>
              </View>
            ))}
            {item.tags.length > 3 && (
              <Text style={styles.moreTagsText}>+{item.tags.length - 3} more</Text>
            )}
          </View>
        )}
      </View>

      {/* Card Footer with Actions */}
      <View style={styles.cardFooter}>
        <View style={styles.cardStats}>
          <View style={styles.statItem}>
            <Ionicons 
              name={item.isClaimed ? "checkmark-circle-outline" : "help-circle-outline"} 
              size={16} 
              color={item.isClaimed ? "#16A34A" : "#6B7280"} 
            />
            <Text style={[styles.statText, { color: item.isClaimed ? "#16A34A" : "#6B7280" }]}>
              {item.isClaimed ? "Verified" : "Unverified"}
            </Text>
          </View>
          
          {item.isYearlyRecurring && (
            <View style={styles.statItem}>
              <Ionicons name="repeat-outline" size={16} color="#2563EB" />
              <Text style={styles.statText}>Recurring</Text>
            </View>
          )}

          {item.cost && (
            <View style={styles.statItem}>
              <Ionicons name="cash-outline" size={16} color="#6B7280" />
              <Text style={styles.statText}>{item.cost}</Text>
            </View>
          )}
        </View>

        <View style={styles.cardActions}>
          <TouchableOpacity
            style={styles.editButton}
            onPress={() => onEditOpportunity(item.id)}
            activeOpacity={0.7}
          >
            <Ionicons name="pencil-outline" size={16} color="#2563EB" />
            <Text style={styles.editButtonText}>Edit</Text>
          </TouchableOpacity>
          
          {featuredOpportunityId === item.id ? (
            <TouchableOpacity
              style={[styles.featuredButton, styles.featuredButtonActive]}
              onPress={handleRemoveFeatured}
              activeOpacity={0.7}
              disabled={isSettingFeatured}
            >
              <Ionicons name="star" size={16} color="#F59E0B" />
              <Text style={[styles.featuredButtonText, styles.featuredButtonTextActive]}>Featured</Text>
            </TouchableOpacity>
          ) : (
            <TouchableOpacity
              style={styles.featuredButton}
              onPress={() => handleSetFeatured(item.id)}
              activeOpacity={0.7}
              disabled={isSettingFeatured}
            >
              <Ionicons name="star-outline" size={16} color="#6B7280" />
              <Text style={styles.featuredButtonText}>Set Featured</Text>
            </TouchableOpacity>
          )}
          
          <TouchableOpacity
            style={styles.deleteButton}
            onPress={() => {
              console.log("Delete button pressed for", item.id, item.title);
              handleDeleteOpportunity(item);
            }}
            activeOpacity={0.7}
          >
            <Ionicons name="trash-outline" size={16} color="#DC2626" />
            <Text style={styles.deleteButtonText}>Delete</Text>
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );

  return (
    <SafeAreaView style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <View>
          <Text style={styles.title}>Manage Opportunities</Text>
          <Text style={styles.subtitle}>
            {filteredOpportunities.length} of {opportunities.length} opportunities
          </Text>
        </View>
        <View style={styles.headerActions}>
          <TouchableOpacity
            style={styles.refreshButton}
            onPress={handleRefresh}
            disabled={isRefreshing}
            activeOpacity={0.7}
          >
            <Ionicons 
              name="refresh" 
              size={20} 
              color={isRefreshing ? "#9CA3AF" : "#6B7280"} 
            />
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.addButton}
            onPress={onCreateOpportunity}
            activeOpacity={0.7}
          >
            <Ionicons name="add" size={20} color="#FFFFFF" />
            <Text style={styles.addButtonText}>Add New</Text>
          </TouchableOpacity>
        </View>
      </View>

      {/* Error Banner */}
      {error && (
        <View style={styles.errorBanner}>
          <Ionicons name="warning-outline" size={18} color="#DC2626" />
          <Text style={styles.errorText}>{error}</Text>
          <TouchableOpacity onPress={() => setError(null)}>
            <Ionicons name="close" size={18} color="#DC2626" />
          </TouchableOpacity>
        </View>
      )}

      {/* Search and Filters */}
      <View style={styles.filtersSection}>
        <View style={styles.searchContainer}>
          <Ionicons name="search-outline" size={20} color="#6B7280" />
          <TextInput
            style={styles.searchInput}
            placeholder="Search opportunities..."
            value={searchQuery}
            onChangeText={setSearchQuery}
            placeholderTextColor="#9CA3AF"
          />
          {searchQuery.length > 0 && (
            <TouchableOpacity onPress={() => setSearchQuery("")}>
              <Ionicons name="close-circle" size={20} color="#6B7280" />
            </TouchableOpacity>
          )}
        </View>

        {renderStatusFilter()}
      </View>

      {/* Content */}
      {isLoading ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color="#2563EB" />
          <Text style={styles.loadingText}>Loading opportunities...</Text>
        </View>
      ) : error ? (
        <View style={styles.errorContainer}>
          <Ionicons name="alert-circle-outline" size={48} color="#DC2626" />
          <Text style={styles.errorTitle}>Error Loading Data</Text>
          <Text style={styles.errorMessage}>{error}</Text>
          <TouchableOpacity
            style={styles.retryButton}
            onPress={loadOpportunities}
            activeOpacity={0.7}
          >
            <Text style={styles.retryButtonText}>Try Again</Text>
          </TouchableOpacity>
        </View>
      ) : filteredOpportunities.length === 0 ? (
        <View style={styles.emptyContainer}>
          <Ionicons name="document-outline" size={64} color="#9CA3AF" />
          <Text style={styles.emptyTitle}>No opportunities found</Text>
          <Text style={styles.emptyText}>
            {searchQuery.trim() !== "" || statusFilter !== "All"
              ? "Try adjusting your search criteria."
              : 'Create your first opportunity by clicking "Add New".'}
          </Text>
          {(searchQuery.trim() !== "" || statusFilter !== "All") && (
            <TouchableOpacity
              style={styles.clearFiltersButton}
              onPress={() => {
                setSearchQuery("");
                setStatusFilter("All");
              }}
              activeOpacity={0.7}
            >
              <Text style={styles.clearFiltersText}>Clear Filters</Text>
            </TouchableOpacity>
          )}
        </View>
      ) : (
        <FlatList
          data={filteredOpportunities}
          renderItem={renderItem}
          keyExtractor={(item) => item.id}
          contentContainerStyle={styles.listContent}
          showsVerticalScrollIndicator={false}
          refreshControl={
            <RefreshControl
              refreshing={isRefreshing}
              onRefresh={handleRefresh}
              tintColor="#2563EB"
            />
          }
        />
      )}

      {/* Delete Request Modal */}
      <Modal
        visible={isDeleteModalVisible}
        transparent
        animationType="fade"
        onRequestClose={() => setIsDeleteModalVisible(false)}
      >
        <View style={dialogStyles.overlay}>
          <View style={dialogStyles.dialogContainer}>
            <View style={dialogStyles.header}>
              <Text style={dialogStyles.title}>Request Deletion</Text>
            </View>
            <View style={dialogStyles.content}>
              <Text style={dialogStyles.message}>
                Submit a deletion request for "{opportunityToDelete?.title}".
                A super admin must approve before it is removed.
              </Text>
              <TextInput
                style={dialogStyles.input}
                placeholder="Optional reason"
                placeholderTextColor="#9CA3AF"
                value={deleteReason}
                onChangeText={setDeleteReason}
                multiline
              />
            </View>
            <View style={dialogStyles.actions}>
              <TouchableOpacity
                style={dialogStyles.cancelButton}
                onPress={() => setIsDeleteModalVisible(false)}
              >
                <Text style={dialogStyles.cancelText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={dialogStyles.confirmButton}
                onPress={submitDeletionRequest}
                disabled={isSubmittingDeleteRequest}
              >
                {isSubmittingDeleteRequest ? (
                  <ActivityIndicator size="small" color="#FFFFFF" />
                ) : (
                  <Text style={dialogStyles.confirmText}>Submit</Text>
                )}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#F9FAFB",
  },
  
  // Header
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingHorizontal: 20,
    paddingVertical: 20,
    backgroundColor: "#FFFFFF",
    borderBottomWidth: 1,
    borderBottomColor: "#E5E7EB",
  },
  title: {
    fontSize: 24,
    fontWeight: "700",
    color: "#111827",
    fontFamily: "Inter-Bold",
  },
  subtitle: {
    fontSize: 14,
    color: "#6B7280",
    marginTop: 4,
    fontFamily: "Inter-Regular",
  },
  headerActions: {
    flexDirection: "row",
    alignItems: "center",
  },
  refreshButton: {
    width: 40,
    height: 40,
    borderRadius: 8,
    backgroundColor: "#F9FAFB",
    alignItems: "center",
    justifyContent: "center",
    borderWidth: 1,
    borderColor: "#E5E7EB",
    marginRight: 12,
  },
  addButton: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#2563EB",
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 8,
  },
  addButtonText: {
    color: "#FFFFFF",
    marginLeft: 6,
    fontSize: 14,
    fontWeight: "600",
    fontFamily: "Inter-SemiBold",
  },

  // Error Banner
  errorBanner: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 20,
    paddingVertical: 12,
    backgroundColor: "#FEF2F2",
    borderWidth: 1,
    borderColor: "#FECACA",
    marginHorizontal: 20,
    marginVertical: 8,
    borderRadius: 8,
  },
  errorText: {
    fontSize: 14,
    color: "#DC2626",
    flex: 1,
    marginHorizontal: 8,
    fontFamily: "Inter-Medium",
  },

  // Filters Section
  filtersSection: {
    padding: 20,
    backgroundColor: "#FFFFFF",
    borderBottomWidth: 1,
    borderBottomColor: "#E5E7EB",
  },
  searchContainer: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#F9FAFB",
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: "#E5E7EB",
  },
  searchInput: {
    flex: 1,
    fontSize: 16,
    color: "#111827",
    marginLeft: 8,
    fontFamily: "Inter-Regular",
  },
  statusFilterContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    marginHorizontal: -4,
  },
  statusFilterButton: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    backgroundColor: "#FFFFFF",
    marginHorizontal: 4,
    marginBottom: 8,
  },
  statusFilterButtonActive: {
    backgroundColor: "#EEF2FF",
    borderColor: "#2563EB",
  },
  statusFilterText: {
    fontSize: 14,
    color: "#6B7280",
    fontFamily: "Inter-Medium",
  },
  statusFilterTextActive: {
    color: "#2563EB",
    fontWeight: "600",
    fontFamily: "Inter-SemiBold",
  },

  // List Content
  listContent: {
    padding: 20,
  },

  // Opportunity Card
  opportunityCard: {
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 3,
    elevation: 3,
  },
  cardHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: "#F3F4F6",
  },
  cardTitleSection: {
    flex: 1,
    marginRight: 12,
  },
  opportunityTitle: {
    fontSize: 18,
    fontWeight: "600",
    color: "#111827",
    marginBottom: 4,
    fontFamily: "Inter-SemiBold",
  },
  organizationText: {
    fontSize: 14,
    color: "#6B7280",
    fontFamily: "Inter-Medium",
  },
  cardHeaderActions: {
    alignItems: "flex-end",
  },
  statusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    borderWidth: 1,
  },
  statusText: {
    fontSize: 12,
    fontWeight: "600",
    fontFamily: "Inter-SemiBold",
  },

  // Card Content
  cardContent: {
    padding: 16,
  },
  detailsRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    marginBottom: 12,
    marginHorizontal: -4,
  },
  detailBadge: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#F9FAFB",
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
    marginHorizontal: 4,
    marginBottom: 4,
  },
  detailText: {
    fontSize: 12,
    color: "#6B7280",
    marginLeft: 4,
    fontFamily: "Inter-Medium",
  },
  descriptionPreview: {
    fontSize: 14,
    color: "#6B7280",
    lineHeight: 20,
    marginBottom: 12,
    fontFamily: "Inter-Regular",
  },
  tagsRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    marginHorizontal: -2,
  },
  tag: {
    backgroundColor: "#EEF2FF",
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 10,
    marginHorizontal: 2,
    marginBottom: 4,
  },
  tagText: {
    fontSize: 11,
    color: "#2563EB",
    fontFamily: "Inter-Medium",
  },
  moreTagsText: {
    fontSize: 11,
    color: "#9CA3AF",
    marginLeft: 4,
    alignSelf: "center",
    fontFamily: "Inter-Regular",
  },

  // Card Footer
  cardFooter: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: 16,
    borderTopWidth: 1,
    borderTopColor: "#F3F4F6",
  },
  cardStats: {
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
  },
  statItem: {
    flexDirection: "row",
    alignItems: "center",
    marginRight: 16,
  },
  statText: {
    fontSize: 12,
    color: "#6B7280",
    marginLeft: 4,
    fontFamily: "Inter-Medium",
  },
  cardActions: {
    flexDirection: "row",
    alignItems: "center",
  },
  editButton: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#EEF2FF",
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
    marginRight: 8,
  },
  editButtonText: {
    fontSize: 12,
    color: "#2563EB",
    marginLeft: 4,
    fontFamily: "Inter-SemiBold",
  },
  deleteButton: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#FEF2F2",
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
  },
  deleteButtonText: {
    fontSize: 12,
    color: "#DC2626",
    marginLeft: 4,
    fontFamily: "Inter-SemiBold",
  },

  // Loading States
  loadingContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 40,
  },
  loadingText: {
    marginTop: 12,
    color: "#6B7280",
    fontSize: 16,
    fontFamily: "Inter-Medium",
  },

  // Error States
  errorContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 40,
  },
  errorTitle: {
    fontSize: 20,
    fontWeight: "600",
    color: "#111827",
    marginTop: 16,
    marginBottom: 8,
    fontFamily: "Inter-SemiBold",
  },
  errorMessage: {
    fontSize: 16,
    color: "#6B7280",
    textAlign: "center",
    marginBottom: 24,
    lineHeight: 24,
    fontFamily: "Inter-Regular",
  },
  retryButton: {
    backgroundColor: "#2563EB",
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 8,
  },
  retryButtonText: {
    color: "#FFFFFF",
    fontSize: 14,
    fontWeight: "600",
    fontFamily: "Inter-SemiBold",
  },

  // Empty States
  emptyContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 40,
  },
  emptyTitle: {
    fontSize: 20,
    fontWeight: "600",
    color: "#111827",
    marginTop: 16,
    marginBottom: 8,
    fontFamily: "Inter-SemiBold",
  },
  emptyText: {
    fontSize: 16,
    color: "#6B7280",
    textAlign: "center",
    marginBottom: 24,
    lineHeight: 24,
    fontFamily: "Inter-Regular",
  },
  clearFiltersButton: {
    backgroundColor: "#F9FAFB",
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: "#E5E7EB",
  },
  clearFiltersText: {
    color: "#6B7280",
    fontSize: 14,
    fontWeight: "500",
    fontFamily: "Inter-Medium",
  },
  
  // Featured Button Styles
  featuredButton: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#F9FAFB",
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
    marginRight: 8,
    borderWidth: 1,
    borderColor: "#E5E7EB",
  },
  featuredButtonActive: {
    backgroundColor: "#FFFBEB",
    borderColor: "#F59E0B",
  },
  featuredButtonText: {
    fontSize: 12,
    color: "#6B7280",
    marginLeft: 4,
    fontFamily: "Inter-SemiBold",
  },
  featuredButtonTextActive: {
    color: "#F59E0B",
  },
  dialogOverlay: {
    flex: 1,
  },
});

const dialogStyles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    justifyContent: "center",
    alignItems: "center",
  },
  dialogContainer: {
    width: "85%",
    maxWidth: 480,
    backgroundColor: "white",
    borderRadius: 12,
    elevation: 5,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.25,
    shadowRadius: 8,
  },
  header: {
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: "#E5E7EB",
  },
  title: {
    fontSize: 18,
    fontWeight: "600",
    color: "#111827",
    fontFamily: "Inter-SemiBold",
  },
  content: {
    padding: 20,
  },
  message: {
    fontSize: 14,
    color: "#6B7280",
    fontFamily: "Inter-Regular",
    lineHeight: 20,
    marginBottom: 12,
  },
  input: {
    minHeight: 80,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    borderRadius: 8,
    padding: 12,
    fontSize: 14,
    color: "#111827",
    fontFamily: "Inter-Regular",
  },
  actions: {
    flexDirection: "row",
    justifyContent: "flex-end",
    padding: 20,
    borderTopWidth: 1,
    borderTopColor: "#E5E7EB",
  },
  cancelButton: {
    paddingVertical: 8,
    paddingHorizontal: 16,
    marginRight: 12,
  },
  cancelText: {
    fontSize: 14,
    color: "#6B7280",
    fontFamily: "Inter-Medium",
  },
  confirmButton: {
    backgroundColor: "#2563EB",
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 6,
  },
  confirmText: {
    fontSize: 14,
    color: "white",
    fontWeight: "500",
    fontFamily: "Inter-Medium",
  },
});

export default OpportunityListScreen;
